<template>
  <div class="betzone">
    <div v-if="betZoneFeatures.length > 1" class="betzone-tabs">
      <button
        v-for="tab in visibleTabs"
        :key="tab.value"
        class="betzone-tab"
        :class="{ active: activeTab === tab.value }"
        @click="activeTab = tab.value"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-for="feature in visibleFeatures" :key="feature.value" :class="feature.wrapperClass">
      <component :is="feature.component" v-bind="feature.props"/>
    </div>
  </div>
</template>

<script>
import config from '@/config'
import Screen from '@/components/mixins/Screen'
import PopularParlays from '@/components/sports/bettingoffer/popularParlays/PopularParlays.vue'
import PlayerPropsWidgetComponent from '@/components/sports/bettingoffer/playerPropsWidget/PlayerPropsWidgetComponent.vue'
import FlashBetting from '@/components/sports/bettingoffer/FlashBetting.vue'
import store from '@/store'
import lib from '@/library'

const DEFAULT_FEATURE_ORDER = 999

const FEATURE_COMPONENTS = {
  flashBetting: FlashBetting,
  popularParlays: PopularParlays,
  playerProps: PlayerPropsWidgetComponent
}

const FEATURE_WRAPPERS = {
  flashBetting: 'flash-betting-container-coupon'
}

export default {
  name: 'BetZone',

  mixins: [Screen],

  data () {
    return {
      activeTab: 'all',
      lastFlashBettingSubscriptionKey: ''
    }
  },

  computed: {
    liveEvents () {
      const events = store.getters['livebettingState/liveEvents']

      return Array.isArray(events) ? events : []
    },

    betZoneTabsConfig () {
      const betzoneConfig = config.app.autoconf.BETZONE

      if (
        !betzoneConfig ||
        !betzoneConfig.tabs ||
        typeof betzoneConfig.tabs !== 'object'
      ) {
        return {}
      }

      return betzoneConfig.tabs
    },

    betZoneFeatures () {
      return Object.keys(this.betZoneTabsConfig)
        .reduce((features, featureKey) => {
          const tabConfig = this.betZoneTabsConfig[featureKey]
          const component = FEATURE_COMPONENTS[featureKey]

          if (
            !tabConfig ||
            typeof tabConfig !== 'object' ||
            tabConfig.enabled !== true ||
            !component ||
            (
              featureKey === 'flashBetting' &&
              this.flashBettingSubscriptionEvents.length === 0
            )
          ) {
            return features
          }

          const parsedOrder = Number(tabConfig.order)

          features.push({
            value: featureKey,
            label: tabConfig.label || featureKey,
            order: Number.isNaN(parsedOrder)
              ? DEFAULT_FEATURE_ORDER
              : parsedOrder,
            component,
            wrapperClass: FEATURE_WRAPPERS[featureKey] || ''
          })

          return features
        }, [])
        .sort((featureA, featureB) => {
          if (featureA.order !== featureB.order) {
            return featureA.order - featureB.order
          }

          return featureA.value.localeCompare(featureB.value)
        })
    },

    visibleTabs () {
      if (this.betZoneFeatures.length <= 1) {
        return this.betZoneFeatures
      }

      return [
        {
          value: 'all',
          label: 'All',
          order: 0
        },
        ...this.betZoneFeatures
      ]
    },

    visibleFeatures () {
      const features = this.activeTab === 'all'
        ? this.betZoneFeatures
        : this.betZoneFeatures.filter(feature => {
          return feature.value === this.activeTab
        })

      return features.map(feature => {
        return {
          ...feature,
          props: this.getFeatureProps(feature.value)
        }
      })
    },

    isFlashBettingEnabled () {
      const flashBettingConfig =
        this.betZoneTabsConfig.flashBetting

      return !!(
        flashBettingConfig &&
        typeof flashBettingConfig === 'object' &&
        flashBettingConfig.enabled === true
      )
    },

    flashBettingEvents () {
      if (!this.isFlashBettingEnabled) {
        return []
      }

      return this.liveEvents
        .filter(event => {
          return (
            event &&
            Array.isArray(event.mtcgflags) &&
            event.mtcgflags.includes('FlashBetting')
          )
        })
        .map(event => {
          const eventMarkets = Array.isArray(event.markets)
            ? event.markets
            : []

          const markets = eventMarkets.filter(market => {
            if (
              !market ||
              typeof market !== 'object' ||
              !Array.isArray(market.selections)
            ) {
              return false
            }

            const selections = market.selections

            const isFlashBetting =
              Array.isArray(market.mtcgflag) &&
              market.mtcgflag.includes('FlashBetting')

            const hasOnlyTwoSelections = selections.length === 2

            const hasPrice =
              hasOnlyTwoSelections &&
              selections.every(selection => {
                return (
                  selection &&
                  selection.price !== undefined &&
                  selection.price !== null
                )
              })

            const hasSuspensionTypeNO =
              hasOnlyTwoSelections &&
              selections.some(selection => {
                return (
                  selection &&
                  selection.idfoselectionsuspensiontype === 'N/O'
                )
              })

            return (
              isFlashBetting &&
              market.istradable &&
              hasOnlyTwoSelections &&
              hasPrice &&
              !hasSuspensionTypeNO
            )
          })

          return {
            event,
            markets
          }
        })
    },

    flashBettingSubscriptionEvents () {
      if (!this.isFlashBettingEnabled) {
        return []
      }

      return this.liveEvents.filter(event => {
        return (
          event &&
          Array.isArray(event.mtcgflags) &&
          event.mtcgflags.includes('FlashBetting')
        )
      })
    },
    isLiveWireConnected () {
      return store.getters['livebettingState/isLiveWireConnected']
    },
    liveWireLiveEventsSubscribeCounter () {
      return store.getters['livebettingState/getLiveWireLiveEventsSubscribeCounter']
    }
  },

  watch: {
    flashBettingSubscriptionEvents: {
      immediate: true,
      handler (events) {
        this.subscribeToFlashBettingMarkets(events)
      }
    },
    liveWireLiveEventsSubscribeCounter () {
      this.lastFlashBettingSubscriptionKey = ''

      this.subscribeToFlashBettingMarkets(
        this.flashBettingSubscriptionEvents
      )
    },

    isLiveWireConnected (isConnected) {
      if (!isConnected) {
        this.lastFlashBettingSubscriptionKey = ''

        return
      }

      this.subscribeToFlashBettingMarkets(
        this.flashBettingSubscriptionEvents
      )
    }
  },

  methods: {

    getFeatureProps (feature) {
      switch (feature) {
        case 'flashBetting':
          return {
            flashBettingEvents: this.flashBettingEvents,
            mode: 'betzone'
          }

        case 'popularParlays':
          return {
            displayMode: 'carousel',
            isFeatureTabs: false
          }

        case 'playerProps':
          return {
            isHomePage: true,
            displayMode: 'carousel'
          }

        default:
          return {}
      }
    },

    subscribeToFlashBettingMarkets (events) {
      if (
        !this.isFlashBettingEnabled ||
        lib.WebSocketLiveService.isConnected === false ||
        !Array.isArray(events) ||
        !events.length
      ) {
        return
      }

      const eventIds = events
        .map(event => {
          return event && (
            event.idfoevent ||
            event.id
          )
        })
        .filter(eventId => {
          return (
            eventId !== undefined &&
            eventId !== null
          )
        })

      if (!eventIds.length) {
        return
      }

      const subscriptionKey = eventIds
        .slice()
        .sort()
        .join('|')

      if (
        subscriptionKey ===
        this.lastFlashBettingSubscriptionKey
      ) {
        return
      }

      lib.WebSocketLiveService
        .subscribeToCouponsFlashBettingMarkets(
          eventIds,
          'FlashBetting',
          8
        )

      this.lastFlashBettingSubscriptionKey =
        subscriptionKey
    },
    unsubscribeFromFlashBettingsMarkets () {
      lib.WebSocketLiveService.unsubscribeFromCouponsFlashBettingsMarkets()
    }
  },
  beforeDestroy () {
    this.unsubscribeFromFlashBettingsMarkets()
  }
}
</script>

<style lang="stylus" scoped>
@import "~@/css/config"

.betzone
  padding 47px 0px
  max-width 810px
  margin 0 auto
  @media mobile-big-and-below
    padding 35px 5px
  @media (min-width: 1024px) and (max-width: 1240px)
    max-width 490px
  @media (min-width: 1241px) and (max-width: 1365px)
    max-width 590px
  @media (min-width: 1366px) and (max-width: 1440px)
    max-width 700px
  @media mobile-big-and-below
    margin-right 0
    max-width none

  >>> .v-expansion-panel__header__icon
    display none !important

  >>> .v-expansion-panel__header
    pointer-events none !important
    cursor default !important

.betzone-tabs
  display inline-flex
  align-items center
  gap 4px
  margin-bottom 14px
  max-width 100%
  overflow-x auto
  padding: 5px !important
  position: fixed
  top: 111px
  z-index: 6
  width: 100%
  max-width: 810px
  left: 50%
  margin-left: -490px
  box-sizing: border-box
  background: var(--popular-parlays-content-bgr)
  color: var(--popular-parlays-content-color)

  @media (min-width: 1024px) and (max-width: 1240px)
    max-width 490px
    margin-left -304px
  @media (min-width: 1241px) and (max-width: 1365px)
    max-width 590px !important
    margin-left -353px !important
  @media (min-width: 1366px) and (max-width: 1380px)
    max-width 700px
    margin-left -408px
  @media (min-width: 1381px) and (max-width: 1395px)
    max-width 700px
    margin-left -407px
  @media (min-width: 1396px) and (max-width: 1440px)
    max-width 700px
    margin-left -403px
  @media (min-width: 1440px)
    margin-left -429px
  @media (min-width: 1441px)
    margin-left -490px
  @media mobile-big-and-below
    top 61px
    width 100%
    left 0
    margin-left 0
    max-width none
  @media mobile-small
    padding 2px !important

.betzone-tab
  position relative
  display flex
  align-items center
  justify-content center
  min-width 116px
  height 36px
  padding 0 16px
  border 0
  border-radius 999px
  background transparent
  color #2396FC
  font-size 13px
  font-family: 'Roboto Condensed Bold'
  line-height 1
  white-space nowrap
  cursor pointer
  transition background .18s ease, color .18s ease, box-shadow .18s ease, transform .18s ease

  @media mobile-big-and-below
    min-width 60px
    height 30px

  &:hover
    color #2396FC
    border 1px solid #2396FC

  &.active
    color #fff
    background #2396FC
    box-shadow 0 3px 10px rgba(0, 0, 0, .12)

  &:active
    transform scale(.98)

.betzone >>> .flash-betting-container-coupon ul.market-collapse > li.v-expansion-panel__container > .v-expansion-panel__body
  display flex

.betzone >>> .flashbetting .selection.flash-market-0,
.betzone >>> .flashbetting .selection.flash-market-1
  box-shadow none !important

.betzone >>> .flash-betting-container
  top 50px !important

.betzone >>> .flash-betting-markets
  padding 0 10px 45px 6px !important
  gap 5px !important

  @media mobile-big-and-below
    padding 0 10px 55px 6px !important

.betzone >>> .flash-betting-markets .flash-betting-event-group
  width: clamp(380px, 45vw, 390px) !important
  min-width: clamp(380px, 45vw, 390px) !important
  max-width: clamp(380px, 45vw, 390px) !important

  @media mobile-big-and-below
    width 95% !important
    min-width 95% !important

.betzone >>> .flash-betting-markets .flash-betting-event-group h3
  text-align center !important
  margin-bottom 8px !important

.betzone >>> .flash-betting-container-coupon
  max-width 825px
  margin-bottom 5px

.betzone >>> .popular_parlays_holder
  margin 0 0 5px 0 !important

.betzone >>> .flash-betting-holder-betzone .currently-empty
  height 210px !important

.flash-betting-holder-betzone .flash-betting-event-group .flash-betting-container
  height 100%

.flash-betting-holder-betzone .flash-betting-event-group .currently-empty
  width 100%
  height 100%
  display flex
  flex-direction column
  justify-content center
  align-items center
  box-sizing border-box
</style>



// WEBPACK FOOTER //
// src/components/sports/bettingoffer/BetZone.vue